/**
 * 閉じられる info Alert。
 *
 * proto (`design/js/nav.js` の `injectDismiss`) は **全 `.ecl-alert--info` に右端の
 * × を自動注入**し、閉じた状態を localStorage に残す。 app には同等の仕組みが無く、
 * 常時表示の凡例 alert を閉じられなかった (デザイナー指摘 2026-08-27
 * 「info alert の右端に、X ボタンを表示したい (8080準拠)」)。
 *
 * proto が × を出さない条件もそのまま引き継ぐ:
 * - **modal 配下は対象外** (modal の操作 flow の一部なので閉じさせない)
 * - **操作部品 (ボタン / リンク / 入力) を含む alert は対象外** (文章のみに限る)
 * - `data-alert-persist` 相当の「常時表示」 opt-out
 *
 * app 側はこれを**自動注入でなく明示的な component 選択**にしている。 注入方式だと
 * 「この alert は閉じられる」 が呼び出し側から見えず、 上の 3 条件を DOM 走査で
 * 推測することになるため。 閉じてよい alert だけがこの component を使う。
 *
 * 文言が変わったら再表示する (proto と同じ挙動)。 キーに見出しと本文をつないだ文字列の
 * ハッシュを混ぜているので、 案内内容が更新されれば既に閉じた人にももう一度出る。
 *
 * 見出しは `title`、 本文は `description` に渡し、 children には × だけを置く
 * (UI 辞書 components/alert-placement.md §4「React（DS `Alert`）での組み方」)。
 */
import { useCallback, useState } from 'react'

import { Alert, Box } from '@enechain/ecloud-designsystem'

import { AlertDismissButton } from '~/components/common/AlertDismissButton'
import { CompactAlert } from '~/components/common/CompactAlert'

type Props = {
  /**
   * 永続化キーの安定部分。 画面 + 用途で一意にする (例 `supply48Data.gcLockNote`)。
   * i18n key をそのまま渡すのが分かりやすい。
   */
  storageKey: string
  /** 見出し。 閉じた状態のキーに内容のハッシュを混ぜるため文字列で受ける */
  title: string
  /** 本文 (見出しの後に続く文がある時だけ)。 見出しと同じくハッシュに混ぜる */
  description?: string
  /**
   * 見出し＋短い説明の 1 行の通知は Compact Alert で出す (alert-placement.md §0)。
   * 長い説明は DS `Alert` のまま (既定)。外側の余白は `marginBottom` を Box で当てる。
   */
  compact?: boolean
} & Omit<
  React.ComponentProps<typeof Alert>,
  'children' | 'description' | 'status' | 'title'
>

const STORAGE_PREFIX = 'ebalance-alert-dismissed:'

/**
 * 文言の変化を検出するための軽いハッシュ (proto `nav.js` の `hashStr` と同型)。
 * 衝突しても「閉じたはずの alert がもう一度出る」 だけなので暗号強度は要らない。
 */
function hashMessage(message: string): string {
  let hash = 0
  for (let i = 0; i < message.length; i += 1) {
    hash = (hash << 5) - hash + message.charCodeAt(i)
    hash |= 0
  }
  return Math.abs(hash).toString(36)
}

function buildKey(storageKey: string, message: string): string {
  return `${STORAGE_PREFIX}${storageKey}:h${hashMessage(message)}`
}

/** localStorage が使えない環境 (Safari private 等) では常に表示側へ倒す */
function readDismissed(key: string): boolean {
  try {
    return window.localStorage.getItem(key) === '1'
  } catch {
    return false
  }
}

export const DismissibleInfoAlert = ({
  storageKey,
  title,
  description,
  compact = false,
  ...alertProps
}: Props): React.ReactElement | null => {
  const key = buildKey(storageKey, `${title}${description ?? ''}`)
  /*
   * 「閉じたか」 は key (= storageKey + 文言のハッシュ) に紐づく派生状態なので、
   * **key が変わったら保存状態を読み直す**。 mount 時の 1 回だけで初期化すると、
   * 同じ mount のまま文言が変わった時に閉じたままになる。 実際に起きるのは
   * i18n の言語切替 — 表示言語の確定は初期描画より後なので、 日本語で閉じた後に
   * 英語へ切り替わると英語の文言でも出てこない (レビュー指摘 2026-08-27)。
   * render 中の setState は「props から導出した state の調整」 として React が
   * 認める形 (同じ render pass 内で再実行され、 子には伝播しない)。
   */
  const [dismissed, setDismissed] = useState(() => ({
    key,
    value: readDismissed(key),
  }))
  if (dismissed.key !== key) {
    setDismissed({ key, value: readDismissed(key) })
  }

  const handleDismiss = useCallback(() => {
    setDismissed({ key, value: true })
    try {
      window.localStorage.setItem(key, '1')
    } catch {
      // 永続化だけ諦める (このセッション中は閉じたままになる)
    }
  }, [key])

  if (dismissed.value) return null

  if (compact) {
    return (
      <Box marginBottom={alertProps.marginBottom}>
        <CompactAlert description={description} status="info" title={title}>
          <AlertDismissButton onClick={handleDismiss} />
        </CompactAlert>
      </Box>
    )
  }

  return (
    <Alert
      {...alertProps}
      description={description}
      status="info"
      title={title}
    >
      <AlertDismissButton onClick={handleDismiss} />
    </Alert>
  )
}
